import React, { forwardRef, useRef, useImperativeHandle, Fragment } from "react";
import { QRCode as QRCodeLogo } from "react-qrcode-logo";
import styles from "./QRCode.module.css";

interface QRCodeProps {
    value: string;
    size: number;
    downloadSize?: number;
    hidden?: boolean;
    downloadFileName?: string;
    logoWidthMultiplier? : number;
}

export interface QRCodeHandle {
    download: () => void;
    captureImage: () => Promise<File | null>;
}

const DEFAULT_LOGO_WIDTH_MULTIPLIER = 0.24;
const DEFAULT_FILE_NAME = "qrcode.png";

const QRCode = forwardRef<QRCodeHandle, QRCodeProps>(({
    value,
    size,
    downloadSize = 1080,
    hidden = false,
    downloadFileName = DEFAULT_FILE_NAME,
    logoWidthMultiplier = DEFAULT_LOGO_WIDTH_MULTIPLIER,
}, ref) => {
    const qrRef = useRef<QRCodeLogo>(null);
    const hiddenRef = useRef<HTMLDivElement>(null);

    // Imperative methods exposed via forwardRef
    useImperativeHandle(ref, () => ({
        async download() {
            // qrRef.current?.download();
            const canvas = hiddenRef.current?.querySelector("canvas");
            if (canvas) {
                canvas.toBlob((blob) => {
                    if (blob) {
                        const link = document.createElement("a");
                        link.href = URL.createObjectURL(blob);
                        link.download = downloadFileName;
                        link.click();
                        URL.revokeObjectURL(link.href);
                    }
                });
            }
        },
        async captureImage(): Promise<File | null> {
            const canvas = hiddenRef.current?.querySelector("canvas");
            if (canvas) {
                return new Promise((resolve) => {
                    canvas.toBlob((blob) => {
                        if (blob) {
                            const file = new File([blob], downloadFileName, { type: "image/png" });
                            resolve(file);
                        } else {
                            resolve(null);
                        }
                    });
                });
            }
            return null;
        },
    }));

    return (
        <>
            {/* Hidden QR for higher resolution download */}
            <div ref={hiddenRef} className={styles.hiddenQrContainer}>
                <QRCodeLogo
                    ref={qrRef}
                    value={value}
                    removeQrCodeBehindLogo
                    logoImage="/assets/images/fioh-logo.png"
                    size={downloadSize}
                    logoWidth={downloadSize * logoWidthMultiplier}
                    qrStyle="dots"
                    eyeRadius={10}
                />
            </div>

            {/* Visible QR Code */}
            {!hidden && (
                <QRCodeLogo
                    value={value}
                    removeQrCodeBehindLogo
                    logoImage="/assets/images/fioh-logo.png"
                    size={size}
                    logoWidth={size * logoWidthMultiplier}
                    qrStyle="dots"
                    eyeRadius={10}
                />
            )}
        </>
    );
});

export default QRCode;
